import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { HelpCircle, Plus, Trash2, Edit, Loader2, Save, Eye, EyeOff, CircleCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const makeOption = () => ({ text: '', is_correct: false });
const makeQuestion = () => ({ question: '', options: [makeOption(), makeOption(), makeOption(), makeOption()], explanation: '' });
const EMPTY = { title: '', description: '', category: 'opće znanje', difficulty: 'medium', cover_image_url: '', questions: [makeQuestion()], published: false };

export default function AdminQuizzes() {
  const { authFetch } = useAdmin();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/quizzes?limit=100`);
      const d = await r.json();
      setQuizzes(d.quizzes || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  function validate(f) {
    if (!f.title.trim()) return 'Unesite naziv kviza';
    if (!f.questions.length) return 'Dodajte barem jedno pitanje';
    for (const [i, q] of f.questions.entries()) {
      if (!q.question.trim()) return `Pitanje #${i + 1} nema tekst`;
      const nonEmpty = q.options.filter(o => o.text.trim());
      if (nonEmpty.length < 2) return `Pitanje #${i + 1} mora imati barem 2 odgovora`;
      if (!nonEmpty.some(o => o.is_correct)) return `Pitanje #${i + 1} nema označen točan odgovor`;
    }
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    const err = validate(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const body = { ...form, questions: form.questions.map(q => ({
        ...q, options: q.options.filter(o => o.text.trim()),
      })) };
      const url = editingId ? `${API}/admin/quizzes/${editingId}` : `${API}/admin/quizzes`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await authFetch(url, { method, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Greška');
      toast.success(editingId ? 'Kviz ažuriran' : 'Kviz kreiran');
      setForm(EMPTY); setEditingId(null); setShowForm(false); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  }

  async function togglePublish(q) {
    await authFetch(`${API}/admin/quizzes/${q.id}`, { method: 'PUT', body: JSON.stringify({ ...q, published: !q.published }) });
    load();
  }

  async function remove(id) {
    if (!window.confirm('Obrisati ovaj kviz?')) return;
    await authFetch(`${API}/admin/quizzes/${id}`, { method: 'DELETE' });
    load();
  }

  function startEdit(q) {
    setEditingId(q.id);
    setForm({
      title: q.title, description: q.description || '',
      category: q.category || 'opće znanje', difficulty: q.difficulty || 'medium',
      cover_image_url: q.cover_image_url || '',
      questions: (q.questions || []).map(qq => ({
        ...qq,
        options: [...(qq.options || []), ...Array(Math.max(0, 4 - (qq.options?.length || 0))).fill(null).map(makeOption)].slice(0, 6),
      })),
      published: !!q.published,
    });
    setShowForm(true);
  }

  // Inline helpers
  const setQ = (i, patch) => setForm(f => ({ ...f, questions: f.questions.map((q, j) => j === i ? { ...q, ...patch } : q) }));
  const setOpt = (qi, oi, patch) => setForm(f => ({
    ...f,
    questions: f.questions.map((q, j) => j !== qi ? q : ({
      ...q, options: q.options.map((o, k) => k !== oi ? (patch.is_correct ? { ...o, is_correct: false } : o) : { ...o, ...patch }),
    })),
  }));
  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, makeQuestion()] }));
  const delQuestion = i => setForm(f => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));
  const addOption = qi => setForm(f => ({ ...f, questions: f.questions.map((q, j) => j !== qi ? q : ({ ...q, options: [...q.options, makeOption()] })) }));
  const delOption = (qi, oi) => setForm(f => ({ ...f, questions: f.questions.map((q, j) => j !== qi ? q : ({ ...q, options: q.options.filter((_, k) => k !== oi) })) }));

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-quizzes-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><HelpCircle size={28} className="text-red-500" /> Kvizovi</h1>
          <p className="text-xs text-zinc-400 mt-1">Interaktivni kvizovi s automatskim bodovanjem i objašnjenjima.</p>
        </div>
        <button onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(s => !s); }}
                data-testid="new-quiz-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium">
          <Plus size={14} /> {showForm ? 'Zatvori' : 'Novi kviz'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-5 rounded-xl border border-zinc-800 bg-zinc-900/50 space-y-5" data-testid="quiz-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Naziv kviza" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                   data-testid="quiz-title" className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Cover URL (opcionalno)" value={form.cover_image_url} onChange={e => setForm({ ...form, cover_image_url: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <input placeholder="Kategorija" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                   className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
            <select value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })}
                    className="bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm">
              <option value="easy">Lako</option>
              <option value="medium">Srednje</option>
              <option value="hard">Teško</option>
            </select>
          </div>
          <textarea placeholder="Kratki opis" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={2} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />

          <div className="space-y-4">
            {form.questions.map((q, qi) => (
              <div key={qi} className="rounded-lg border border-zinc-700 bg-zinc-950/60 p-4" data-testid={`quiz-q-${qi}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-red-400">Pitanje {qi + 1}</span>
                  {form.questions.length > 1 && (
                    <button type="button" onClick={() => delQuestion(qi)} className="text-zinc-500 hover:text-red-400" data-testid={`quiz-del-q-${qi}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <input required placeholder="Tekst pitanja" value={q.question} onChange={e => setQ(qi, { question: e.target.value })}
                       className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-2" />
                <div className="space-y-2">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <button type="button" onClick={() => setOpt(qi, oi, { is_correct: true })}
                              data-testid={`quiz-q${qi}-correct-${oi}`}
                              title="Označi točan" className={`p-1.5 rounded ${o.is_correct ? 'bg-emerald-500/20 text-emerald-400' : 'text-zinc-500 hover:text-emerald-400'}`}>
                        <CircleCheck size={14} />
                      </button>
                      <input placeholder={`Odgovor ${oi + 1}`} value={o.text} onChange={e => setOpt(qi, oi, { text: e.target.value })}
                             className="flex-1 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-sm" />
                      {q.options.length > 2 && (
                        <button type="button" onClick={() => delOption(qi, oi)} className="text-zinc-500 hover:text-red-400">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {q.options.length < 6 && (
                    <button type="button" onClick={() => addOption(qi)} className="text-xs text-sky-400 hover:text-sky-300">
                      + Dodaj odgovor
                    </button>
                  )}
                </div>
                <textarea placeholder="Objašnjenje točnog odgovora (opcionalno)" value={q.explanation} onChange={e => setQ(qi, { explanation: e.target.value })}
                          rows={2} className="w-full mt-3 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-xs" />
              </div>
            ))}
            <button type="button" onClick={addQuestion} className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 rounded-md text-sm" data-testid="quiz-add-q">
              <Plus size={12} /> Dodaj pitanje
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} data-testid="quiz-published" />
            <span>Objavi odmah</span>
          </label>
          <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium disabled:opacity-50"
                  data-testid="quiz-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editingId ? 'Spremi kviz' : 'Kreiraj kviz'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="no-quizzes">Još nema kvizova</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="quiz-list">
          {quizzes.map(q => (
            <div key={q.id} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`quiz-card-${q.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-zinc-100">{q.title}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {q.questions?.length || 0} pitanja · {q.difficulty} · {q.plays || 0} igara · prosjek {q.avg_score || 0}%
                  </p>
                </div>
                <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm ${q.published ? 'bg-emerald-600/20 text-emerald-300' : 'bg-zinc-700 text-zinc-400'}`}>
                  {q.published ? 'Objavljeno' : 'Nacrt'}
                </span>
              </div>
              {q.description && <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{q.description}</p>}
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => togglePublish(q)} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-700 rounded hover:border-emerald-500 hover:text-emerald-400" data-testid={`quiz-toggle-${q.id}`}>
                  {q.published ? <><EyeOff size={11} /> Sakrij</> : <><Eye size={11} /> Objavi</>}
                </button>
                <button onClick={() => startEdit(q)} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-700 rounded hover:border-blue-500 hover:text-blue-400" data-testid={`quiz-edit-${q.id}`}>
                  <Edit size={11} /> Uredi
                </button>
                <button onClick={() => remove(q.id)} className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border border-zinc-700 rounded hover:border-red-500 hover:text-red-400" data-testid={`quiz-del-${q.id}`}>
                  <Trash2 size={11} /> Briši
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
